'use client';
import { ArrowLeft, ArrowRight } from '@ethsign/icons';
import { Button, Modal, ScrollArea } from '@ethsign/ui';

import localforage from 'localforage';

import { useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useLocalStorage } from 'react-use';

import { ColorCheckCircleIcon, Loading } from '@/components';
import { ContractSendTypeModal } from '@/components/Modal/ContractSendTypeModal';
import { ShareContractModal } from '@/components/Modal/ShareContractModal';
import { WithoutPasswordModal } from '@/components/Modal/WithoutPasswordModal';
import { StepTab } from '@/components/StepTab';
import { TemplateAddFields } from '@/components/TemplateAddFields';
import { useRouter, useSearchParams, useUserInfo } from '@/hooks';
import { IContact, SendType } from '@/types';
import { deleteOtherLocalStorage, reportGAEvent } from '@/utils';

import { useViewModel } from '@/utils/bizify';

import { PageCtrl } from './PageCtrl';

import { AddField } from './components/AddField';
import { InviteSigners } from './components/InviteSigners';
import { ReviewAndSend } from './components/ReviewAndSend';
import { UploadContract } from './components/UploadContract';

export default function PreparePage() {
  const { id = '' } = useParams();
  const { folderId, templateId } = useSearchParams();
  const creatingRef = useRef(false);
  const vm = useViewModel(PageCtrl);
  const router = useRouter();
  const vmData = vm.$useSnapshot();
  const { xfdfString, loading, fileType, sendType, contractName, contractType, templateData, currentStep } = vmData;
  const [cacheData] = useLocalStorage<any>(id!, {});
  const [file, setFile] = useState<any>(null);
  const { user } = useUserInfo();
  const addFieldRef = useRef<any>(null);
  const setData = (data: Record<string, any>) => {
    vm.setData(data);
    const cache = JSON.parse(localStorage.getItem(id) || '') || {};
    localStorage.setItem(id, JSON.stringify({ ...cache, ...data, annotations: [] }));
  };

  useEffect(() => {
    if (!cacheData?.templateData && templateId) {
      setData({ currentStep: 1 });
      vm.getContractTemplate(templateId).then((res) => {
        setData(res);
      });
    } else {
      setData({
        ...cacheData,
        recipients: cacheData?.recipients?.length > 0 ? cacheData?.recipients : []
      });
    }
  }, [templateId]);

  useEffect(() => {
    const getFile = async () => {
      localforage.config({
        driver: localforage.INDEXEDDB
      });
      const file = (await localforage.getItem(id)) as File[];
      if (!file) return;
      setFile(file);
      setData({
        contractName:
          cacheData.contractName || file[0]?.name.replace(/.pdf/gi, '') /* currentStep: vmData.currentStep || 1 */
      });
    };
    getFile();
    deleteOtherLocalStorage(id);
  }, [id]);

  const steps =
    sendType === SendType.link
      ? [
          {
            title: 'Upload Contract'
          },
          {
            title: 'Add Fields'
          },
          {
            title: 'Review and Send'
          }
        ]
      : [
          {
            title: 'Upload Contract'
          },
          {
            title: 'Invite Signers'
          },
          {
            title: 'Add Fields'
          },
          {
            title: 'Review and Send'
          }
        ];
  if (templateId) {
    steps.splice(1, 0, {
      title: 'Edit Terms'
    });
  }
  const getStep = (step: number) => {
    return (sendType === SendType.invite ? step : step - 1) + (templateId ? 1 : 0);
  };
  const onCreate = async () => {
    if (creatingRef.current) {
      return;
    }
    creatingRef.current = true;
    try {
      const file = await addFieldRef.current?.getFile();
      const result = await vm.onCreate({ user, file, folderId, temId: id, templateId });
      if (result) {
        creatingRef.current = false;
        if (sendType === SendType.link) {
          vm.setShareModalVisible(true);
        } else {
          router.push('/dashboard');
        }
      }
      creatingRef.current = false;
    } catch (error) {
      console.error(error);
      creatingRef.current = false;
    }
  };
  const onNext = async (sendType?: SendType) => {
    const result = await vm.onNext({ file, sendType, setFile, id });
    if (result) {
      if (vmData.currentStep === getStep(3)) {
        onCreate();
        return;
      }
      // 如果是第一步，且sendType发生变化，清空所有的annotation
      if (vmData.currentStep === 0 && vmData.sendType !== sendType) {
        addFieldRef.current?.deleteAllAnnotations();
      }
      setData({
        currentStep: vmData.currentStep + 1,
        sendType: sendType || vmData.sendType
      });
    }
  };

  const onPrev = () => {
    if (currentStep === 0) {
      router.replace('/dashboard');
      return;
    } else if (currentStep === 1 && templateData) {
      router.replace(`/prepare/temp${Date.now()}`);
    }

    if (vmData.currentStep === getStep(2)) {
      addFieldRef.current?.refreshFile();
    }
    setData({ currentStep: vmData.currentStep - 1 });
  };
  const accounts = vmData.recipients.map((item: any) => ({
    ...item,
    name: item.name,
    address: item.walletAddress
  }));

  const onUpload = async (file: File) => {
    await vm.onUpload(file, user!, id);
    setFile([file]);
    setData({
      contractName: file ? file.name.replace(/.pdf|.docx|.doc/gi, '') : '',
      fileType: vm.$getState().fileType
    });
  };

  return (
    <div className="bg-white h-full flex flex-col overflow-hidden">
      <div className="flex items-center justify-center px-[40px] border-b border-gray-100 min-h-[72px] text-lg font-medium text-gray-700 relative">
        <div
          className="flex items-center gap-[8px] cursor-pointer  absolute left-10"
          onClick={() => router.push('/dashboard')}
        >
          <ArrowLeft color={'#344054'} size={16} />
          <span>Dashboard</span>
        </div>
        <StepTab items={steps} current={vmData.currentStep} />

        {/* <div className="cursor-pointer">Save as draft</div> */}
      </div>
      {currentStep === 1 && templateId && (
        <div className="flex-1 overflow-hidden">
          {loading && <Loading />}
          {templateData && (
            <div className="h-full pb-[82px]">
              <TemplateAddFields
                data={templateData}
                isShow={true}
                type={sendType}
                isEditTerms={true}
                onDataChange={(data) => {
                  setData({
                    templateData: data
                  });
                }}
              />
            </div>
          )}
        </div>
      )}
      {file && (
        <AddField
          isUseTemplate={!!templateId}
          fields={templateData?.fields as any}
          contractType={contractType}
          sendType={sendType}
          ref={addFieldRef}
          contractName={contractName}
          fileType={fileType}
          accounts={accounts}
          file={file[0]}
          blankFile={file[1]}
          xfdfString={xfdfString}
          onChange={(xfdfString: any, annotations: any) => {
            const data = localStorage.getItem(id);
            const cache = (data && JSON.parse(data)) || {};
            setData({ ...cache, xfdfString, annotations } as any);
          }}
          onContractTypeChange={(type) => {
            vm.setData({ contractType: type, xfdfString: '' });
          }}
          isShow={vmData.currentStep === getStep(2)}
        />
      )}
      {vmData.currentStep !== getStep(2) && !(currentStep === 1 && templateData) && (
        <ScrollArea className="px-[90px] py-[24px] pb-0 h-[calc(100%-156px)]">
          {vmData.currentStep === 0 && (
            <UploadContract id={id} file={file?.[0]} onUpload={onUpload} folderId={folderId} />
          )}
          {vmData.currentStep === getStep(1) && sendType === SendType.invite ? (
            <InviteSigners
              data={vmData.recipients as IContact[]}
              onChange={(newData) => {
                setData({ recipients: newData });
              }}
              vmData={vmData}
            />
          ) : null}
          {vmData.currentStep === getStep(3) ? (
            <ReviewAndSend
              data={{
                sendType: vmData.sendType,
                recipients: vmData.recipients,
                expireDate: vmData.expireDate,
                password: vmData.password,
                enableExpireDate: vmData.enableExpireDate,
                enablePassword: vmData.enablePassword,
                contractName: vmData.contractName,
                extraMessage: vmData.extraMessage,
                expireTimeError: vmData.expireTimeError,
                viewers: vmData.viewers
              }}
              setData={setData}
            />
          ) : null}
        </ScrollArea>
      )}
      <div className="fixed z-40 bg-white w-full bottom-0 px-[40px] py-[20px] flex justify-between items-center shadow-xs border border-top-1 border-gray-100">
        <Button variant="outline" onClick={onPrev}>
          <ArrowLeft color={'#344054'} size={16} /> Back
        </Button>
        <Button onClick={() => onNext()} loading={loading}>
          Next <ArrowRight color={'#FFFFFF'} size={16} />
        </Button>
      </div>
      <WithoutPasswordModal
        open={vmData.passwordModalVisible}
        onOpenChange={vm.setPasswordModalVisible}
        onConfirm={() => {
          onCreate();
        }}
        onCancel={() => {
          vm.setPasswordModalVisible(false);
          setData({ enablePassword: true });
        }}
      />
      <ContractSendTypeModal
        open={vmData.contractSendTypeModalOpen}
        onOpenChange={vm.setContractSendTypeModalOpen}
        onConfirm={(sendType) => {
          onNext(sendType);
          reportGAEvent('pc_contract_chooseContractType', {
            sendType: sendType as string,
            address: user!.walletAddress!,
            tempId: id
          });
        }}
      />
      <Modal open={false} footer={false} className={'md:w-[339px] max-w-max'}>
        <div className="flex items-center flex-col justify-center">
          <ColorCheckCircleIcon />
          <div className="text-lg font-medium text-gray-900 mt-[20px] text-center">Success</div>
          <div className={'text-sm text-gray-500 text-center mt-2'}>Your contract has been sent successfully.</div>
          <Button variant="ghost" className={'text-primary-900 hover:text-primary-900 mt-6 w-full'}>
            Share Contract Viewing Link
          </Button>
        </div>
      </Modal>
      <ShareContractModal
        type={'signer'}
        open={vmData.shareModalVisible}
        onOpenChange={(v) => {
          vm.setShareModalVisible(v);
          if (!v) {
            router.push('/dashboard');
          }
        }}
        expireDate={vmData.enableExpireDate ? vmData.expireDate : undefined}
        contractId={vmData.contractId}
        password={vmData.enablePassword ? vmData.password : undefined}
      />
    </div>
  );
}
